import { useEffect, useRef, useState } from 'react';
import { BtnModalCloseX, Button, ButtonStyles, CheckBox } from './ui';

/*============================*
 | INSURANCE PICKER MODAL     |
 *============================*/

const InsurancePickerModal = ({ insuranceTypes, useSelection, onCancel, onAccept }) => {
    const pickerRef = useRef(null);
    const containerRef = useRef(null);
    const compactorRef = useRef(null);
    const checkBoxWrapper = useRef(null);

    const [selection, setSelection] = useSelection();
    const [startingSelection] = useState(selection);

    useEffect(() => {
        pickerRef.current.focus();
        const handleClickOutside = event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                onCancel();
            }
        };
        // Close modal when clicking outside of it.
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Track modal resize and adjust grid columns accordingly
    useEffect(() => {
        function calculateColumnWidths(cellWidths, numColumns) {
            // Initialize an array to store the max width of each column
            const columnWidths = new Array(numColumns).fill(0);

            // Iterate through each cell width
            cellWidths.forEach((width, index) => {
                const columnIndex = index % numColumns; // Determine the current column index
                // Update the column width if the current cell's width is greater than the stored column width
                columnWidths[columnIndex] = Math.max(columnWidths[columnIndex], width);
            });

            return columnWidths;
        }

        const colGap = window.getComputedStyle(checkBoxWrapper.current).getPropertyValue('column-gap');
        const handleResize = () => {
            if (compactorRef.current) {
                const modal = containerRef.current;
                const compactor = compactorRef.current;
                const picker = pickerRef.current;
                if (modal && compactor && picker) {
                    let modalWidth = modal.getBoundingClientRect().width;
                    // subtract padding from modal width
                    modalWidth -= parseFloat(window.getComputedStyle(modal).paddingLeft);
                    modalWidth -= parseFloat(window.getComputedStyle(modal).paddingRight);

                    const inputs = modal.querySelectorAll('.check-box-container');
                    // reset input widths
                    inputs.forEach(inp => {
                        inp.style.width = 'fit-content';
                        inp.style.overflow = 'visible';
                    });

                    const inputsWidthList = Array.from(inputs).map(inp => {
                        return inp.getBoundingClientRect().width;
                    });
                    // loop each input calculate max number of columns that can fit in the modal
                    let columns = 1;

                    // find maximum number of columns that can fit in the modal
                    // no one row can be wider than the modal
                    // test each number of columns until the total width of all inputs in a row is greater than the modal width
                    let looping = true;
                    // if all inputs can fit in one row, set columns to inputs.length
                    if (inputsWidthList.reduce((acc, cur) => acc + cur, 0) < modalWidth) {
                        columns = inputs.length;
                        looping = false;
                    }

                    while (looping) {
                        let thisRowWidth = 0;
                        columns++;
                        // calculate total width of all inputs in a row
                        const rowWidths = calculateColumnWidths(inputsWidthList, columns);

                        thisRowWidth = rowWidths.reduce((acc, cur) => acc + cur, 0);
                        // add column-gap to thisRowWidth
                        thisRowWidth += (columns - 1) * parseFloat(colGap);

                        // if total width is greater than modal width, exit loop and set columns to one less
                        if (thisRowWidth > modalWidth) {
                            looping = false;
                            columns--;
                        }
                        if (columns > 50) {
                            // prevent infinite loop
                            looping = false;
                        }
                    }

                    // set grid-template-columns based on number of columns
                    if (columns - 1 <= 0) {
                        columns = 1;
                    }

                    checkBoxWrapper.current.style.gridTemplateColumns = `repeat(${columns}, 1fr)`;

                    if ((columns = 1)) {
                        // prevent any cell from being wider than the modal
                        // above logic will try to resolve the width issue until there is only one column
                        for (let i = 1; i < inputsWidthList.length; i++) {
                            if (inputsWidthList[i] > modalWidth) {
                                // stlye wide inputs to fit in the modal
                                inputs[i].style.width = `${modalWidth}px`;
                                inputs[i].style.overflow = 'hidden';
                            }
                        }
                    }
                }
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleCancel = () => {
        // Reset selection to starting selection if user cancels
        setSelection(startingSelection);
        onCancel();
    };
    const handleAccept = () => {
        onAccept();
    };

    const handleCheckboxChange = (e, option) => {
        let thisSelection = [...selection];
        thisSelection = thisSelection.filter(selected => selected.name !== option.name);

        if (e.target.checked) {
            thisSelection.push(option);
        }
        setSelection(thisSelection);
    };

    return (
        <div className="insurance-picker-compactor" ref={compactorRef}>
            <div role="group" className="insurance-picker-modal" ref={containerRef}>
                <div>
                    <h4 ref={pickerRef} tabIndex={-1}>
                        <span className="insurance-picker-heading-tablet">
                            Select all insurance types that apply to your business
                        </span>
                        <span className="insurance-picker-heading-mobile">Select Insurance Type</span>
                    </h4>
                    <BtnModalCloseX onClick={handleCancel} />
                </div>
                <div className="check-box-wrapper" ref={checkBoxWrapper}>
                    {insuranceTypes.map((option, idx) => (
                        <div key={idx} className="check-box-container">
                            <CheckBox
                                id={`insurance-type-${option.id}-${idx}`}
                                value={selection.find(selected => selected.name === option.name)}
                                checkHandler={e => handleCheckboxChange(e, option)}
                            />
                            <label className="check-box-label" htmlFor={`insurance-type-${option.id}-${idx}`}>
                                {option.name}
                            </label>
                        </div>
                    ))}
                </div>
                <div className="insurance-picker-actions">
                    <Button btn-action="cancel" onClick={handleCancel} btnStyle={ButtonStyles.SECONDARY}>
                        CANCEL
                    </Button>
                    <Button onClick={handleAccept}>Select Options</Button>
                </div>
            </div>
        </div>
    );
};

export default InsurancePickerModal;
